import * as React from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EMPTY } from '@/lib/format';
import { Input } from '@/components/ui/input';

export interface InlineTextProps {
  value: string | null | undefined;
  onCommit: (value: string | null) => void | Promise<unknown>;
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
}

/** Click-to-edit text for table cells (Go/No-go queue reason). Enter/blur saves, Esc cancels. */
export function InlineText({
  value,
  onCommit,
  label,
  placeholder,
  readOnly = false,
  className,
}: InlineTextProps) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value ?? '');
  const [saving, setSaving] = React.useState(false);

  const commit = async () => {
    setEditing(false);
    const next = draft.trim() === '' ? null : draft.trim();
    if (next === (value ?? null)) return;
    setSaving(true);
    try {
      await onCommit(next);
    } finally {
      setSaving(false);
    }
  };

  if (readOnly) {
    return <span className={cn(!value && 'text-muted-foreground', className)}>{value || EMPTY}</span>;
  }
  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        placeholder={placeholder}
        aria-label={label}
        className="h-8 min-w-[10rem]"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(value ?? '');
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <button
      type="button"
      aria-label={`Edit ${label}`}
      className={cn(
        'group -mx-1.5 inline-flex max-w-full items-center gap-1.5 rounded px-1.5 py-0.5 text-left hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        saving && 'opacity-60',
        className,
      )}
      disabled={saving}
      onClick={() => {
        setDraft(value ?? '');
        setEditing(true);
      }}
    >
      <span className={cn('truncate', !value && 'text-muted-foreground')}>
        {value || placeholder || EMPTY}
      </span>
      <Pencil
        className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden="true"
      />
    </button>
  );
}
